// Whether *we* have booted the Intercom widget for this page load.
//
// This is deliberately module-level state, not a React ref or piece of
// component state: `window.Intercom`'s booted status is a page-lifetime
// browser singleton, and the two things that call `boot()` — the
// anonymous-visitor auto-boot fallback and the logged-in identify flow
// (`useIdentifyMetrics`) — are mounted as mutually exclusive sibling
// components (see `BackendServiceProvider`), so a React ref on either one
// can't be shared with the other. Tracking it here means both sides agree on
// the *real* boot state instead of guessing from elapsed time, which is what
// let the widget boot anonymously out from under an earlier version's
// org-gate `shutdown()` call — see the history on
// `providers/CustomIntercomStateProvider.tsx`.
//
// Monotonic: nothing calls `shutdown()` any more (the org/plan rule now only
// hides the launcher — see `utils/intercomLauncherVisibility.ts` — so
// Intercom stays booted for every logged-in user, keeping `trackEvent` and
// explicit "Chat with us" entry points working regardless of the rule). Once
// true, this stays true for the rest of the page load.
let isBooted = false;

// Subscribers for `useHideIntercom` (and friends): react-use-intercom's own
// `update()` silently no-ops on a widget that isn't booted yet, so a page
// that asks to hide the launcher *before* boot has happened loses that
// request with no way to know it needs to retry. Notifying once boot happens
// lets those callers re-apply their hide the moment it would actually take
// effect, without needing to know anything about *why* the widget just
// booted (route, org, plan — none of that belongs here).
type BootListener = () => void;
const listeners = new Set<BootListener>();

export function isIntercomBooted(): boolean {
  return isBooted;
}

export function markIntercomBooted(): void {
  if (isBooted) return;
  isBooted = true;
  listeners.forEach(listener => listener());
}

export function onIntercomBoot(listener: BootListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Test-only: reset the module singleton between test cases. Never call this
// from app code.
export function __resetIntercomBootStateForTests(): void {
  isBooted = false;
  listeners.clear();
}
